import { Layout } from '../components/Layout';
import { Link } from 'react-router-dom';
import { ArrowLeft, Users, Mail } from 'lucide-react';

export default function CommunityGuidelinesPage() {
  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/"
            className="inline-flex items-center text-[var(--theme-text-secondary)] hover:text-[var(--theme-text-primary)] mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <Users className="w-8 h-8 text-[#C9956C]" />
            <h1 className="text-3xl font-bold text-[var(--theme-text-primary)]">Community Guidelines</h1>
          </div>
          <p className="text-[var(--theme-text-secondary)]">Last updated: April 2026</p>
        </div>

        {/* Content */}
        <div className="prose prose-slate max-w-none space-y-8 text-[var(--theme-text-primary)]">
          <section>
            <p className="text-[var(--theme-text-secondary)] leading-relaxed">
              WANTD is an Australian marketplace built on trust. These guidelines set the
              standard for how we expect every member to behave. Breaking them can result
              in warnings, temporary suspension, or permanent removal from the platform.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[var(--theme-text-primary)] mb-4">1. Be honest</h2>
            <ul className="text-[var(--theme-text-secondary)] leading-relaxed list-disc list-inside space-y-2">
              <li>List items accurately. Use your own photos and describe condition truthfully.</li>
              <li>Don't misrepresent what you're selling or wanting.</li>
              <li>If you're listing on behalf of someone else, say so.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[var(--theme-text-primary)] mb-4">2. Be respectful</h2>
            <ul className="text-[var(--theme-text-secondary)] leading-relaxed list-disc list-inside space-y-2">
              <li>No harassment, hate speech, discrimination, or threats of any kind.</li>
              <li>Disagreements happen — handle them calmly, or walk away.</li>
              <li>Assume good faith until there's a clear reason not to.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[var(--theme-text-primary)] mb-4">3. No prohibited items</h2>
            <p className="text-[var(--theme-text-secondary)] leading-relaxed mb-3">
              You cannot list, request, or trade:
            </p>
            <ul className="text-[var(--theme-text-secondary)] leading-relaxed list-disc list-inside space-y-2">
              <li>Weapons, firearms, ammunition, or replica firearms</li>
              <li>Illegal drugs, drug paraphernalia, or prescription medication</li>
              <li>Stolen goods or items you don't have legal right to sell</li>
              <li>Counterfeit or knock-off branded goods</li>
              <li>Live animals, animal products, or anything breaching wildlife law</li>
              <li>Adult content or services</li>
              <li>Tobacco, vaping products, or alcohol sold to unlicensed buyers</li>
              <li>Anything prohibited under Australian law</li>
            </ul>
            <p className="text-[var(--theme-text-secondary)] leading-relaxed mt-3">
              See our{' '}
              <Link to="/terms" className="text-[#C9956C] hover:underline">Terms of Service</Link>{' '}
              for the full list.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[var(--theme-text-primary)] mb-4">4. Protect your privacy</h2>
            <ul className="text-[var(--theme-text-secondary)] leading-relaxed list-disc list-inside space-y-2">
              <li>Don't share your phone number, email, or home address outside the platform until you're ready to meet.</li>
              <li>Use in-app messaging until you've agreed to a deal.</li>
              <li>Don't ask other members for ID or personal information they haven't offered.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[var(--theme-text-primary)] mb-4">5. Meet safely</h2>
            <ul className="text-[var(--theme-text-secondary)] leading-relaxed list-disc list-inside space-y-2">
              <li>Meet in public places during daylight where possible.</li>
              <li>Bring a friend for higher-value items.</li>
              <li>Use a WANTD pre-authorisation hold for secure transactions — it protects both parties.</li>
              <li>Trust your instincts. If something feels off, don't proceed.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[var(--theme-text-primary)] mb-4">6. Trade fairly</h2>
            <ul className="text-[var(--theme-text-secondary)] leading-relaxed list-disc list-inside space-y-2">
              <li>Honour the commitments you make.</li>
              <li>Don't pull out of a deal at the last minute without good reason.</li>
              <li>Complete transactions through WANTD where fees apply — don't try to move off-platform to dodge them.</li>
              <li>Leave honest reviews based on your actual experience.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[var(--theme-text-primary)] mb-4">7. No spam or scams</h2>
            <ul className="text-[var(--theme-text-secondary)] leading-relaxed list-disc list-inside space-y-2">
              <li>One listing per item. Don't repost the same listing multiple times.</li>
              <li>No pyramid schemes, chain posts, or referral spam.</li>
              <li>No impersonation of other users, brands, or WANTD staff.</li>
              <li>Don't use bots or automation to scrape listings or mass-message members.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[var(--theme-text-primary)] mb-4">8. Report problems</h2>
            <p className="text-[var(--theme-text-secondary)] leading-relaxed">
              If you see a listing, message, or member that breaks these guidelines, use
              the Report button on the item, profile, or message. Our team reviews every
              report. False reports made in bad faith may themselves result in a warning.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[var(--theme-text-primary)] mb-4">9. What happens when rules are broken</h2>
            <ul className="text-[var(--theme-text-secondary)] leading-relaxed list-disc list-inside space-y-2">
              <li><strong>First offence (minor):</strong> Warning and request to change behaviour.</li>
              <li><strong>Second offence or serious breach:</strong> Temporary suspension (typically 7 days).</li>
              <li><strong>Severe or repeat offences:</strong> Permanent account removal.</li>
              <li><strong>Illegal activity:</strong> Referral to relevant Australian authorities.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[var(--theme-text-primary)] mb-4">10. Australian Consumer Law</h2>
            <p className="text-[var(--theme-text-secondary)] leading-relaxed">
              Nothing in these guidelines overrides your rights under the Australian
              Consumer Law. Private-sale consumer protections still apply between members.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[var(--theme-text-primary)] mb-4">Questions?</h2>
            <div className="p-4 rounded-xl bg-[var(--theme-surface)] border border-[var(--theme-border)]">
              <div className="flex items-center gap-2 text-[var(--theme-text-primary)]">
                <Mail className="w-5 h-5 text-[#C9956C]" />
                <a href="mailto:support@wantd.au" className="text-[#C9956C] hover:underline">support@wantd.au</a>
              </div>
              <p className="mt-2 text-sm text-[var(--theme-text-secondary)]">
                WANTD reserves the right to update these guidelines. Material changes will
                be notified to all members.
              </p>
            </div>
          </section>
        </div>

        {/* Footer Links */}
        <div className="mt-12 pt-8 border-t border-[var(--theme-border)]">
          <div className="flex flex-wrap gap-6 text-sm">
            <Link to="/terms" className="text-[#C9956C] hover:underline">Terms of Service</Link>
            <Link to="/privacy" className="text-[#C9956C] hover:underline">Privacy Policy</Link>
            <Link to="/" className="text-[#C9956C] hover:underline">Back to WANTD</Link>
          </div>
        </div>
      </div>
    </Layout>
  );
}
